import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";

/**
 * CookieBanner — slim GDPR consent bar, bottom-left, shows once per browser.
 * Choice stored in localStorage ('ix.cookie.consent' → 'all' | 'essential').
 * Never re-appears once dismissed. Respects prefers-reduced-motion via Framer.
 */
const STORAGE_KEY = "ix.cookie.consent";

export default function CookieBanner() {
  const { t } = useTranslation();
  const { prefix } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay slightly so the loader / hero can settle before the bar slides in.
    const t = setTimeout(() => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
      } catch (_) {
        /* private mode: don't nag */
      }
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  const decide = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() }),
      );
    } catch (_) {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.45, ease: [0.76, 0, 0.24, 1] }}
          className="fixed z-[90] bottom-4 left-4 right-4 md:right-auto md:max-w-[420px] rounded-2xl bg-forest text-canvas p-5 md:p-6 shadow-[0_24px_70px_-30px_rgba(74,29,150,0.7)] border border-canvas/10"
        >
          <button
            onClick={() => decide("essential")}
            aria-label="Dismiss"
            className="absolute top-3 right-3 w-7 h-7 rounded-full grid place-items-center text-canvas/60 hover:text-canvas hover:bg-canvas/10 transition-colors"
            data-testid="cookie-banner-close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>

          <div className="text-[10px] tracking-[0.28em] uppercase text-lime font-bold">
            Cookies
          </div>
          <h2 className="mt-2 font-display text-canvas text-xl md:text-2xl tracking-tight leading-snug">
            {t("cookies.heading")}<span className="italic font-serif"> {t("cookies.headingItalic")}</span>
          </h2>
          <p className="mt-2 text-sm text-canvas/70 leading-relaxed">
            {t("cookies.body")}{" "}
            <Link
              to={prefix("/legal/cookies")}
              className="underline underline-offset-2 text-lime hover:text-canvas transition-colors"
              data-testid="cookie-banner-policy"
            >
              {t("cookies.privacy")}
            </Link>
            .
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => decide("all")}
              className="inline-flex items-center gap-1.5 bg-lime text-ink px-4 py-2 rounded-full text-sm font-semibold hover:bg-lime/90 transition-colors"
              data-testid="cookie-banner-accept-all"
            >
              {t("cookies.acceptAll")}
            </button>
            <button
              onClick={() => decide("essential")}
              className="inline-flex items-center gap-1.5 border border-canvas/25 text-canvas px-4 py-2 rounded-full text-sm hover:bg-canvas/10 transition-colors"
              data-testid="cookie-banner-essential"
            >
              {t("cookies.onlyEssential")}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
